(function () {
  'use strict';

  const SETTINGS = {
    gramsPerKg: 0.3,
    gramsPerScoop: 2,
    singleGrams: 250,
    doubleGrams: 500,
    doubleDoseAboveKg: 25,
    firstDiscount: 0.25,
    recurringDiscount: 0.05
  };

  const TABLE_WEIGHTS = [
    5, 7.5, 10, 12.5, 15, 17.5, 20, 22.5, 25, 27.5,
    30, 32.5, 35, 37.5, 40, 42.5, 45, 47.5, 50
  ];

  const money = value =>
    Number(value).toLocaleString('de-DE', {
      style: 'currency',
      currency: 'EUR'
    });

  const numberDE = (value, decimals = 2) =>
    Number(value).toLocaleString('de-DE', {
      minimumFractionDigits: 0,
      maximumFractionDigits: decimals
    });

  const weeksLabel = value =>
    `${numberDE(value, 1)} ${Number(value) === 1 ? 'Woche' : 'Wochen'}`;

  const packLabel = isDouble =>
    isDouble ? '2 × 250 g' : '250 g';

  function formatScoops(value) {
    const rounded = Math.ceil(value * 4) / 4;
    const whole = Math.floor(rounded);
    const fraction = Math.round((rounded - whole) * 4);
    const fractions = ['', '¼', '½', '¾'];
    return fraction
      ? `${whole ? whole + ' ' : ''}${fractions[fraction]}`
      : String(whole);
  }

  function getProductData(root) {
    return {
      single: {
        price: Number(root.dataset.singlePrice || 0) / 100,
        id: String(root.dataset.singleVariantId || '')
      },
      double: {
        price: Number(root.dataset.doublePrice || 0) / 100,
        id: String(root.dataset.doubleVariantId || '')
      }
    };
  }

  function getSellingPlans(root) {
    try {
      const data = JSON.parse(
        root.querySelector('[data-aw-selling-plans]')?.textContent || '{}'
      );
      return {
        single: Array.isArray(data.single) ? data.single : [],
        double: Array.isArray(data.double) ? data.double : []
      };
    } catch (error) {
      console.warn('Alpenwuff: Selling Plans konnten nicht gelesen werden.', error);
      return { single: [], double: [] };
    }
  }

  function intervalInWeeks(name = '') {
    const text = String(name).toLowerCase().replace(',', '.');
    const patterns = [
      [/(\d+(?:\.\d+)?)\s*(?:woche|wochen|week|weeks|wk)\b/, 1],
      [/(\d+(?:\.\d+)?)\s*(?:tag|tage|tagen|day|days)\b/, 1 / 7],
      [/(\d+(?:\.\d+)?)\s*(?:monat|monate|monaten|month|months)\b/, 4.345]
    ];

    for (const [regex, factor] of patterns) {
      const match = text.match(regex);
      if (match) return Number(match[1]) * factor;
    }
    return null;
  }

  function closestPlan(plans, targetWeeks) {
    if (!plans?.length) return null;

    const normalized = plans.map(plan => ({
      id: String(plan.id || ''),
      name: String(plan.name || ''),
      weeks: intervalInWeeks(plan.name)
    }));

    const measurable = normalized.filter(plan => Number.isFinite(plan.weeks));
    if (!measurable.length) return normalized[0];

    return measurable.reduce((best, current) =>
      Math.abs(current.weeks - targetWeeks) < Math.abs(best.weeks - targetWeeks)
        ? current
        : best
    );
  }

  function calculate(weight, productData) {
    const dailyGrams = weight * SETTINGS.gramsPerKg;
    const doubleDose = weight > SETTINGS.doubleDoseAboveKg;
    const variant = doubleDose ? productData.double : productData.single;
    const totalGrams = doubleDose ? SETTINGS.doubleGrams : SETTINGS.singleGrams;
    const supplyDays = totalGrams / dailyGrams;
    const deliveryWeeks = Math.max(1, Math.round(supplyDays / 7));
    const firstOrder = variant.price * (1 - SETTINGS.firstDiscount);
    const recurringOrder = variant.price * (1 - SETTINGS.recurringDiscount);

    return {
      weight,
      dailyGrams,
      scoops: dailyGrams / SETTINGS.gramsPerScoop,
      doubleDose,
      variant,
      supplyDays,
      deliveryWeeks,
      firstOrder,
      recurringOrder
    };
  }

  function display(values) {
    return {
      weight: numberDE(values.weight, 1),
      grams: `${numberDE(values.dailyGrams, 2)} g`,
      scoops: formatScoops(values.scoops),
      pack: packLabel(values.doubleDose),
      interval: weeksLabel(values.deliveryWeeks),
      normalDaily: money(values.variant.price / values.supplyDays),
      firstDaily: money(values.firstOrder / values.supplyDays),
      recurringDaily: money(values.recurringOrder / values.supplyDays),
      normalOrder: money(values.variant.price),
      firstOrder: money(values.firstOrder),
      recurringOrder: money(values.recurringOrder)
    };
  }

  function setText(root, map) {
    Object.entries(map).forEach(([selector, value]) => {
      root.querySelectorAll(selector).forEach(el => {
        el.textContent = value;
      });
    });
  }

  function setMessage(root, text = '', error = false) {
    const el = root.querySelector('[data-cart-message]');
    if (!el) return;
    el.textContent = text;
    el.classList.toggle('is-error', error);
  }

  function updateSlider(slider) {
    const min = Number(slider.min);
    const max = Number(slider.max);
    const value = Number(slider.value);
    const pct = ((value - min) / (max - min)) * 100;

    slider.style.background =
      `linear-gradient(to right,#596950 0%,#596950 ${pct}%,#d5d1c8 ${pct}%,#d5d1c8 100%)`;
  }

  function planLabel(plan, fallbackWeeks) {
    if (Number.isFinite(plan?.weeks)) return `alle ${weeksLabel(plan.weeks)}`;
    return plan?.name || `alle ${weeksLabel(fallbackWeeks)}`;
  }

  function updateCalculator(root) {
    const slider = root.querySelector('[data-slider]');
    if (!slider) return;

    const productData = getProductData(root);
    const plans = getSellingPlans(root);
    const values = calculate(Number(slider.value), productData);
    const shown = display(values);

    const variantPlans = values.doubleDose ? plans.double : plans.single;
    const plan = closestPlan(variantPlans, values.deliveryWeeks);
    const selectedPlanLabel = planLabel(plan, values.deliveryWeeks);

    setText(root, {
      '[data-weight]': shown.weight,
      '[data-grams]': shown.grams,
      '[data-scoops]': shown.scoops,
      '[data-pack]': shown.pack,
      '[data-interval]': shown.interval,
      '[data-normal-price]': shown.normalDaily,
      '[data-first-daily-price]': shown.firstDaily,
      '[data-recurring-daily-price]': shown.recurringDaily,
      '[data-normal-order-price]': shown.normalOrder,
      '[data-first-order-price]': shown.firstOrder,
      '[data-recurring-order-price]': shown.recurringOrder,
      '[data-cart-pack]': shown.pack,
      '[data-cart-plan-label]': selectedPlanLabel,
      '[data-cart-order-price]': shown.firstOrder,
      '[data-cart-normal-order-price]': shown.normalOrder
    });

    const variantInput = root.querySelector('[data-cart-variant-id]');
    const planInput = root.querySelector('[data-cart-selling-plan]');
    const button = root.querySelector('[data-add-subscription]');
    const valid = Boolean(values.variant.id && plan?.id);

    if (variantInput) variantInput.value = values.variant.id;
    if (planInput) planInput.value = plan?.id || '';

    if (button) {
      button.disabled = !valid;
      button.innerHTML = valid
        ? `Flex-Abo für <span data-cart-order-price>${shown.firstOrder}</span> <span class="aw-calc-add-button-old-price">(statt <span data-cart-normal-order-price>${shown.normalOrder}</span>)</span> in den Warenkorb`
        : 'Flex-Abo aktuell nicht verfügbar';
    }

    setMessage(
      root,
      valid ? '' : 'Für die empfohlene Variante ist aktuell kein passender Flex-Abo-Plan hinterlegt.',
      !valid
    );

    updateSlider(slider);
  }

  function updateTable(root) {
    const tbody = root.querySelector('[data-feeding-table-body]');
    if (!tbody) return;

    const productData = getProductData(root);
    const plans = getSellingPlans(root);

    tbody.innerHTML = TABLE_WEIGHTS.map(weight => {
      const values = calculate(weight, productData);
      const shown = display(values);
      const variantPlans = values.doubleDose ? plans.double : plans.single;
      const plan = closestPlan(variantPlans, values.deliveryWeeks);

      return `
        <tr>
          <td>${shown.weight} kg</td>
          <td>${shown.grams}</td>
          <td>${shown.scoops}</td>
          <td>${shown.pack}</td>
          <td>${planLabel(plan, values.deliveryWeeks)}</td>
          <td>${shown.firstDaily}</td>
          <td>${shown.recurringDaily}</td>
          <td>${shown.normalDaily}</td>
        </tr>
      `;
    }).join('');
  }

  function shopifyRoot() {
    return window.Shopify?.routes?.root || '/';
  }

  function openCartDrawer() {
    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.show === 'function') {
      drawer.show();
      return true;
    }

    const trigger =
      document.querySelector('[data-action="open-drawer"][data-drawer-id="sidebar-cart"]') ||
      document.querySelector('[aria-label="Open cart"]');

    if (trigger) {
      trigger.click();
      return true;
    }

    return false;
  }

  async function addSubscription(form, root) {
    const variantId = form.querySelector('[data-cart-variant-id]')?.value;
    const sellingPlan = form.querySelector('[data-cart-selling-plan]')?.value;
    const button = form.querySelector('[data-add-subscription]');

    if (!variantId || !sellingPlan) {
      setMessage(root, 'Variante oder Flex-Abo-Plan konnte nicht korrekt ermittelt werden.', true);
      return;
    }

    const originalHtml = button?.innerHTML || '';
    if (button) {
      button.disabled = true;
      button.textContent = 'Wird in den Warenkorb gelegt …';
    }
    setMessage(root);

    try {
      const response = await fetch(`${shopifyRoot()}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          items: [{
            id: variantId,
            quantity: 1,
            selling_plan: sellingPlan
          }]
        })
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.description || data?.message || 'Das Flex-Abo konnte nicht in den Warenkorb gelegt werden.');
      }

      document.documentElement.dispatchEvent(
        new CustomEvent('cart:refresh', {
          bubbles: true,
          detail: data
        })
      );

      if (button) button.textContent = 'Im Warenkorb ✓';

      window.setTimeout(() => {
        if (!openCartDrawer()) setMessage(root, 'Zum Warenkorb hinzugefügt.');
      }, 120);

      window.setTimeout(() => {
        if (button) {
          button.disabled = false;
          button.innerHTML = originalHtml;
        }
      }, 1500);

    } catch (error) {
      console.error('Alpenwuff Flex-Abo Add-to-Cart:', error);
      if (button) {
        button.disabled = false;
        button.innerHTML = originalHtml;
      }
      setMessage(root, error?.message || 'Beim Hinzufügen zum Warenkorb ist ein Fehler aufgetreten.', true);
    }
  }

  function initialize(scope = document) {
    const roots = [
      ...(scope.matches?.('[data-aw-topping-calculator]') ? [scope] : []),
      ...(scope.querySelectorAll?.('[data-aw-topping-calculator]') || [])
    ];

    [...new Set(roots)].forEach(root => {
      updateCalculator(root);
      updateTable(root);
    });
  }

  document.addEventListener('input', event => {
    const slider = event.target.closest?.('[data-slider]');
    const root = slider?.closest('[data-aw-topping-calculator]');
    if (root) updateCalculator(root);
  });

  document.addEventListener('submit', event => {
    const form = event.target.closest?.('[data-subscription-form]');
    if (!form) return;

    event.preventDefault();

    const root = form.closest('[data-aw-topping-calculator]');
    if (root) addSubscription(form, root);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(document));
  } else {
    initialize(document);
  }

  document.addEventListener('shopify:section:load', event => initialize(event.target));
})();
